import { ref } from 'vue'
import router from '@/router'

/** Pending open from a mention notification. Shared so the chart can consume it after navigation. */
export const pendingOpen = ref(null)

let requestId = 0
let openHandler = null

/**
 * Decide what the org chart should open for this request.
 * `unanchoredReply` is a board-level reply mention: no position, and the mention is not the top-level comment.
 * @param {object|null} request Pending open.
 * @param {string} planBoardId Scenario id of the mounted chart.
 * @returns {{type: string, commentId: string|null, positionId: string|null, messageId: string|null, unanchoredReply: boolean}|null} Action, or null when this chart should ignore it.
 */
export function takeOpen(request, planBoardId) {
  if (!request || request.planBoardId !== planBoardId) return null
  const messageId = request.messageId || request.commentId || null
  const unanchoredReply = Boolean(
    !request.positionId && request.commentId && messageId && messageId !== request.commentId
  )
  if (request.commentId || request.positionId) {
    return {
      type: 'thread',
      commentId: request.commentId,
      positionId: request.positionId,
      messageId,
      unanchoredReply
    }
  }
  return { type: 'panel', commentId: null, positionId: null, messageId, unanchoredReply: false }
}

/**
 * Clear the pending open once it is handled. A newer click keeps its request.
 * @param {number} requestId Request that finished.
 */
export function clearOpen(requestId) {
  if (pendingOpen.value?.requestId === requestId) pendingOpen.value = null
}

/**
 * Register who opens a pending comment.
 * A click while someone is registered opens immediately.
 * A click before that is delivered on the next bind.
 * @param {Function} handler Receives the pending open request.
 * @returns {Function} Unsubscribe.
 */
export function bindCommentOpen(handler) {
  openHandler = handler
  if (pendingOpen.value) handler(pendingOpen.value)
  return () => {
    if (openHandler === handler) openHandler = null
  }
}

/**
 * Navigate to the scenario and remember which comment to open.
 * The chart may not be mounted yet, so the request stays in `pendingOpen`.
 * Each call gets a new request id so a repeat click still opens.
 * @param {object} target Scenario and comment ids.
 * @param {string} target.boardId Main board id.
 * @param {string} target.planBoardId Scenario id.
 * @param {string|null} [target.commentId] Thread root id.
 * @param {string|null} [target.messageId] Comment to scroll into view. A reply id when the mention is on a reply.
 * @param {string|null} [target.positionId] Position id when the comment is on a node.
 * @returns {boolean} True when a request was recorded.
 */
export function openComment({
  boardId,
  planBoardId,
  commentId = null,
  messageId = null,
  positionId = null
}) {
  if (!boardId || !planBoardId) return false
  Promise.resolve(
    router.push({
      name: 'Plan View',
      params: { boardId, planId: planBoardId },
      query: { mode: 'org' }
    })
  ).catch(() => {})
  requestId += 1
  pendingOpen.value = {
    planBoardId,
    commentId: commentId || null,
    messageId: messageId || commentId || null,
    positionId: positionId || null,
    requestId
  }
  openHandler?.(pendingOpen.value)
  return true
}
